<script lang="ts">
  import { onMount, SvelteComponent, setContext } from "svelte";
  import { ROUTE } from "../constants/route-constants.js";
  import { LABEL } from "../constants/label-constants.js";
  import type { Project, Customer } from "../services/api/types.js";
  import { getProject, getCustomer } from "../services/api/data-handler.js";
  import { addDrawer, setDrawerVisibility } from "../services/stores/side-drawer-store.js";
  import { addObject, editObject } from "../services/stores/object-drawer-store.js";
  import { currentRoute, type RouteData } from "../services/routing/route-store.js";
  import { userStore } from "../services/stores/user-store.js";
  import NavBar from "../components/common/NavBar.svelte";
  import Breadcrumb from "../components/common/Breadcrumb.svelte";
  import SideDrawerSystem from "../components/drawers/SideDrawerSystem.svelte";
  import HelpDrawer from "../components/drawers/HelpDrawer.svelte";
  import ObjectDrawerSystem from "../components/drawers/ObjectDrawerSystem.svelte";
  import HomeContent from "../content/Home.svelte";
  import ProjectsContent from "../content/Projects.svelte";
  import ProjectContent from "../content/Project.svelte";
  import CustomersContent from "../content/Customers.svelte";
  import CustomerContent from "../content/Customer.svelte";
  import ViewResults from "../content/ViewResults.svelte";
  import UsersContent from "../content/Users.svelte";
  import UnknownContent from "./Unknown.svelte";
  import LookupsContent from "../content/Lookups.svelte";
  import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
  import { checkRouteAccess, logUnauthorizedAccess } from "../services/security/route-guard.js";
  import { logError, logInfo, logWarning } from "../services/logging/log-handler.js";

  let contentComponent: typeof SvelteComponent;
  let breadcrumbItems: { label: string; href?: string }[] = [];
  let userType: string = "";

  // Subscribe to user store to get user type
  userStore.subscribe((value) => {
    userType = value.user?.user_type || "";
  });

  setContext("addObject", addObject);
  setContext("editObject", editObject);

  onMount(() => {
    addDrawer({
      key: "help",
      icon: faInfoCircle,
      component: HelpDrawer,
      title: "Help",
      description: "Help for application.",
      supportsRefresh: false,
      defaultWidth: 900,
    });
    setContent($currentRoute);
    setDrawerVisibility("help", true);
  });

  async function setContent(route: RouteData) {
    const routeName = route.name.toLowerCase();
    const id = route.params?.id;

    // Check route access using the route guard
    const guardResult = checkRouteAccess(route);
    if (!guardResult.isAllowed) {
      logUnauthorizedAccess(route, guardResult);
      if (guardResult.redirectTo) {
        window.location.href = guardResult.redirectTo;
      }
      contentComponent = UnknownContent as typeof SvelteComponent;
      breadcrumbItems = [];
      return;
    }

    let project: Project | undefined;
    let project_id: string;
    let projectName: string;
    let customer: Customer | undefined;
    let customer_id: string;
    let customerName: string;

    switch (routeName) {
      case ROUTE.HOME:
        contentComponent = HomeContent as typeof SvelteComponent;
        breadcrumbItems = [];
        break;

      case ROUTE.PROJECTS:
        contentComponent = ProjectsContent as typeof SvelteComponent;
        breadcrumbItems = [{ label: LABEL.PROJECTS }];
        break;

      case ROUTE.PROJECT:
        contentComponent = ProjectContent as typeof SvelteComponent;
        try {
          project = (await getProject(id)).project;
          if (project) {
            projectName = project.name;
            customer_id = project.customer.customer_id;
            customer = (await getCustomer(customer_id)).customer;
            customerName = customer.name;
            breadcrumbItems = [
              { label: LABEL.CUSTOMERS, href: "/" + ROUTE.CUSTOMERS },
              {
                label: LABEL.CUSTOMER + ": " + customerName,
                href: "/" + ROUTE.CUSTOMER + "?id=" + customer_id,
              },
              { label: LABEL.PROJECT + ": " + projectName },
            ];
          } else {
            throw new Error("Project not found");
          }
        } catch (error) {
          logError(error, "Main", undefined, { message: "Error fetching project" });
          breadcrumbItems = [{ label: LABEL.PROJECTS, href: "/" + ROUTE.PROJECTS }, { label: LABEL.PROJECTS + ": Not Found" }];
        }
        break;

      case ROUTE.CUSTOMERS:
        contentComponent = CustomersContent as typeof SvelteComponent;
        breadcrumbItems = [{ label: LABEL.CUSTOMERS }];
        break;

      case ROUTE.CUSTOMER:
        contentComponent = CustomerContent as typeof SvelteComponent;
        try {
          customer = (await getCustomer(id)).customer;
          if (customer) {
            customerName = customer.name;
            breadcrumbItems = [{ label: LABEL.CUSTOMERS, href: "/" + ROUTE.CUSTOMERS }, { label: LABEL.CUSTOMER + ": " + customerName }];
          } else {
            throw new Error("Customer not found");
          }
        } catch (error) {
          logError(error, "Main", undefined, { message: "Error fetching customer" });
          breadcrumbItems = [{ label: LABEL.CUSTOMERS, href: "/" + ROUTE.CUSTOMERS }, { label: LABEL.CUSTOMER + ": Not Found" }];
        }
        break;

      case ROUTE.RESULTS:
        contentComponent = ViewResults as typeof SvelteComponent;
        try {
          customer = (await getCustomer(id)).customer;
          if (customer) {
            customerName = customer.name;
            // For customer users, breadcrumb items are handled by the Breadcrumb component
            // For OH users, show full navigation breadcrumbs
            if (userType === "customer") {
              breadcrumbItems = []; // Customer name is shown by Breadcrumb component
            } else {
              breadcrumbItems = [{ label: LABEL.CUSTOMERS, href: "/" + ROUTE.CUSTOMERS }, { label: LABEL.CUSTOMER + ": " + customerName }];
            }
          } else {
            throw new Error("Customer not found");
          }
        } catch (error) {
          logError(error, "Main", undefined, { message: "Error fetching customer" });
          breadcrumbItems = [{ label: LABEL.CUSTOMER + ": Not Found" }];
        }
        break;

      case ROUTE.USERS:
        contentComponent = UsersContent as typeof SvelteComponent;
        breadcrumbItems = [{ label: LABEL.USERS }];
        break;

      case ROUTE.LOOKUPS:
        contentComponent = LookupsContent as typeof SvelteComponent;
        breadcrumbItems = [{ label: LABEL.LOOKUPS }];
        break;

      default:
        contentComponent = HomeContent as typeof SvelteComponent;
        breadcrumbItems = [];
    }
  }

  $: if ($currentRoute) {
    setContent($currentRoute);
  }
</script>

<div id="app-container">
  <appbar>
    <NavBar />
  </appbar>
  <div id="content-container">
    <main>
      <Breadcrumb items={breadcrumbItems} />
      {#await contentComponent}
        <p>Loading...</p>
      {:then Component}
        {#if $currentRoute.params?.id}
          <svelte:component this={Component} id={$currentRoute.params.id} />
        {:else}
          <svelte:component this={Component} />
        {/if}
      {:catch error}
        <p>Error loading content: {error.message}</p>
      {/await}
    </main>
    <ObjectDrawerSystem />
    <SideDrawerSystem />
  </div>
</div>
